import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { AbstractControl, FormControl, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { TranslocoModule } from '@ngneat/transloco';
import { TuiButtonModule } from '@taiga-ui/core';
import { BOOKING_TIME_MINUTE_STEP } from '@us/web/cart/util';
import { getHours, isAfter, isBefore, isValid, parseISO } from 'date-fns';
import { BehaviorSubject } from 'rxjs';

function isoStringValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: unknown } | null => {
        const value = control.value;
        if (value === null || value === undefined || value === '') {
            return null; // Don't validate empty values
        }

        // Check if value is a valid ISO string
        if (!isValid(new Date(value))) {
            return { isoString: true }; // Return validation error if value is not a valid ISO string
        }

        return null; // Value is valid ISO string
    };
}

function minDateValidator(min: Date): ValidatorFn {
    return (control: AbstractControl): { [key: string]: unknown } | null => {
        const value = control.value;
        if (value === null || value === undefined || value === '') {
            return null; // Don't validate empty values
        }

        if (isBefore(new Date(value), min)) {
            return { min: true };
        }

        return null;
    };
}

@Component({
    selector: 'us-select-time',
    standalone: true,
    imports: [IonicModule, ReactiveFormsModule, TuiButtonModule, TranslocoModule, AsyncPipe],
    templateUrl: './select-time.component.html',
    styleUrls: ['./select-time.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectTimeComponent {
    readonly min$ = new BehaviorSubject<string | null>(null);
    readonly hourValues$ = new BehaviorSubject<number[]>([]);
    readonly minuteValues$ = new BehaviorSubject<number[]>([]);

    readonly timeCtrl: FormControl<string | null> = new FormControl<string>('', [
        Validators.required,
        isoStringValidator()
    ]);

    @Input() set min(min: string) {
        const minHour = min ? getHours(parseISO(min)) : 0;
        this.hourValues$.next(Array.from({ length: 23 - minHour + 1 }, (_, i) => i + minHour));
        this.minuteValues$.next(
            Array.from({ length: 60 / BOOKING_TIME_MINUTE_STEP }, (_, i) => i * BOOKING_TIME_MINUTE_STEP)
        );
        this.timeCtrl.setValidators([Validators.required, isoStringValidator(), minDateValidator(parseISO(min))]);
        this.min$.next(min);
    }

    @Input() set value(value: string) {
        this.timeCtrl.setValue(value);
    }

    @Output() valueChange = new EventEmitter<string>();

    handleSubmit() {
        const value = this.timeCtrl.value;
        if (value !== null) {
            this.valueChange.emit(value);
        }
    }
}
